import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class FormErrorService {
  getMessages(control: AbstractControl | null): string[] {
    if (!control || !control.errors) return [];

    const messages: string[] = [];
    const errors = control.errors;

    if (errors['required']) messages.push('This field is required.');
    if (errors['min']) messages.push(`Minimum value is ${errors['min'].min}.`);
    if (errors['max']) messages.push(`Maximum value is ${errors['max'].max}.`);
    if (errors['minlength']) {
      messages.push(`Minimum length is ${errors['minlength'].requiredLength}.`);
    }
    if (errors['maxlength']) {
      messages.push(`Maximum length is ${errors['maxlength'].requiredLength}.`);
    }
    if (errors['pattern']) messages.push('Invalid format.');
    if (errors['email']) messages.push('Must be a valid email address.');
    if (errors['match']) messages.push('Fields do not match.');
    if (errors['mismatch']) messages.push('Passwords do not match.');
    if (errors['invalidDate']) messages.push('Invalid date.');
    if (errors['invalidVin']) messages.push('Invalid VIN number.');
    if (errors['invalidDateTime']) messages.push('Enter a valid date and time.');
    if (errors['pastDate']) messages.push('Date and time must be in the future.');
    if (errors['deliveryTooSoon']) {
      messages.push(
        'Dropoff must be at least 1 hour after pickup. If both are on the same day, check the times you selected.',
      );
    }

    if (errors['invalidPhoneNumber']) messages.push('Invalid phone number.');
    if (errors['minimumAge'])
      messages.push('Person must be at least 18 years old.');
    if (errors['expirationDate'])
      messages.push('Date must be at least 10 days ahead.');
    if (errors['custom']) messages.push(errors['custom']);

    return messages;
  }

  getControl(
    form: AbstractControl | null,
    path: string,
  ): AbstractControl | null {
    return form?.get(path) ?? null;
  }

  getControlErrors(form: AbstractControl | null, path: string): string[] {
    return this.getMessages(this.getControl(form, path));
  }

  /**
   * Maps a signal-form / Zod validation `message` to user-facing copy.
   * Schema tokens like `required` match reactive {@link getMessages} wording.
   */
  resolveValidationMessage(message: string | undefined): string | null {
    if (message == null || message.length === 0) {
      return null;
    }

    switch (message) {
      case 'required':
        return 'This field is required.';
      case 'minLength':
        return 'Select at least one option.';
      case 'email':
        return 'Must be a valid email address.';
      case 'pattern':
        return 'Invalid format.';
      case 'invalidDateTime':
        return 'Enter a valid date and time.';
      case 'pastDate':
        return 'Date and time must be in the future.';
      default:
        return message;
    }
  }

  /** Maps Angular signal-form validation errors (objects with optional `message`) to strings. */
  messagesFromValidationErrors(
    errors: readonly { message?: string }[],
  ): string[] {
    const out: string[] = [];
    for (const e of errors) {
      const resolved = this.resolveValidationMessage(e.message);
      if (resolved) {
        out.push(resolved);
      }
    }
    return out;
  }
}
